// Application shell: hash-based routing, the top navigation, the theme switch,
// and one-time store bootstrap. Pages are plain components selected by the route.

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from '@/store';
import type { ThemeName } from '@/charts';
import { parseHash, hasSettingsParams, paramsToSettings } from '@/lib/url';
import { DEFAULT_SETTINGS } from '@/store';
import { Logo } from './Logo';
import { TestPage } from './pages/Test';
import { SweepPage } from './pages/Sweep';
import { MethodologyPage } from './pages/Methodology';
import { SharePage } from './pages/Share';

interface Route {
  path: string;
  params: URLSearchParams;
}

/** Track the current hash route, reacting to back/forward and link clicks. */
function useHashRoute(): Route {
  const [route, setRoute] = useState<Route>(() => parseHash(window.location.hash));
  useEffect(() => {
    const onHash = () => setRoute(parseHash(window.location.hash));
    window.addEventListener('hashchange', onHash);
    if (!window.location.hash) window.location.hash = '#/test';
    return () => window.removeEventListener('hashchange', onHash);
  }, []);
  return route;
}

const NAV = [
  { path: '/test', key: 'nav.test' },
  { path: '/sweep', key: 'nav.sweep' },
  { path: '/methodology', key: 'nav.methodology' },
] as const;

function ThemeSwitch() {
  const { t } = useTranslation();
  const theme = useStore((s) => s.theme);
  const setTheme = useStore((s) => s.setTheme);
  return (
    <label className="themeswitch">
      <span className="sr-only">{t('theme.label')}</span>
      <select
        className="input input--sm"
        value={theme}
        aria-label={t('theme.label')}
        onChange={(e) => setTheme(e.target.value as ThemeName)}
      >
        <option value="light">{t('theme.light')}</option>
        <option value="dark">{t('theme.dark')}</option>
        <option value="high-contrast">{t('theme.high-contrast')}</option>
      </select>
    </label>
  );
}

export default function App() {
  const { t } = useTranslation();
  const route = useHashRoute();
  const init = useStore((s) => s.init);
  const replaceSettings = useStore((s) => s.replaceSettings);

  // One-time bootstrap: worker, nodes, history, theme.
  useEffect(() => {
    void init();
  }, [init]);

  // Apply settings passed in the URL (copy-settings-link) once on first load.
  useEffect(() => {
    const { params } = parseHash(window.location.hash);
    if (hasSettingsParams(params)) {
      replaceSettings(paramsToSettings(params, DEFAULT_SETTINGS));
    }
    // Intentionally runs once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const active = route.path;

  return (
    <div className="app">
      <header className="topbar">
        <a className="brand" href="#/test">
          <Logo size={32} className="brand__mark" />
          <span className="brand__text">
            <span className="brand__name">{t('app.title')}</span>
            <span className="brand__tag">{t('app.tagline')}</span>
          </span>
        </a>
        <nav className="topnav" aria-label="Primary">
          {NAV.map((item) => (
            <a
              key={item.path}
              href={`#${item.path}`}
              className={`topnav__link${active === item.path ? ' is-active' : ''}`}
              aria-current={active === item.path ? 'page' : undefined}
            >
              {t(item.key)}
            </a>
          ))}
        </nav>
        <ThemeSwitch />
      </header>

      <main className="content">
        {active === '/test' && <TestPage />}
        {active === '/sweep' && <SweepPage />}
        {active === '/methodology' && <MethodologyPage />}
        {active === '/share' && <SharePage id={route.params.get('id')} />}
      </main>
    </div>
  );
}
